/**
 * Loads analytics scripts (e.g. Google Analytics) only when the user has
 * consented via the cookie banner. Respects GDPR/CCPA preferences.
 *
 * Set REACT_APP_GA_MEASUREMENT_ID in .env to enable Google Analytics.
 * Scripts are loaded dynamically when consent is given.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  mayLoadAnalytics,
  getStoredPreferences,
  COOKIE_PREFERENCES_CHANGED,
} from '../utils/cookieConsent';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

export const ConditionalAnalytics: React.FC = () => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    const checkAndLoad = () => {
      if (!GA_MEASUREMENT_ID) return;
      if (!mayLoadAnalytics()) return;
      if (loadedRef.current) return;

      loadedRef.current = true;
      setShouldLoad(true);
    };

    checkAndLoad();

    const onPreferencesChanged = () => {
      const prefs = getStoredPreferences();
      if (prefs?.analytics) {
        checkAndLoad();
      }
      // If user revoked consent, we don't unload (GA script already ran).
      // Future page loads will respect the new preference.
    };

    window.addEventListener(COOKIE_PREFERENCES_CHANGED, onPreferencesChanged);
    return () =>
      window.removeEventListener(
        COOKIE_PREFERENCES_CHANGED,
        onPreferencesChanged
      );
  }, []);

  useEffect(() => {
    if (!shouldLoad || !GA_MEASUREMENT_ID) return;

    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    const initScript = document.createElement('script');
    initScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}', { anonymize_ip: true });
    `;
    document.head.appendChild(initScript);
  }, [shouldLoad]);

  return null;
};

export default ConditionalAnalytics;
